/**
 * Tipos para Cuenta Corriente — Fase 7.1
 *
 * Documentos relacionados:
 *   - requerimientos.md § 7.1
 *   - DECISIONES.md #030 (vistas SQL en lugar de tabla materializada)
 *
 * Los datos vienen de dos vistas:
 *   - vw_cuenta_corriente_detalle (una fila por cupón)
 *   - vw_cuenta_corriente_resumen (una fila por empresa)
 *
 * Las vistas usan `security_invoker` → la RLS de cupones/pagos se aplica
 * automáticamente: el admin_empresa solo ve los suyos.
 */

// ── Estados ───────────────────────────────────────────────────────────────────

export type EstadoCtaCteCupon =
  | 'al_dia'
  | 'pendiente'
  | 'parcial'
  | 'vencida';

export type EstadoCtaCteEmpresa =
  | 'al_dia'
  | 'pendiente'
  | 'vencida'
  | 'sin_movimientos';

export function getEstadoCtaCteLabel(estado: string): string {
  const labels: Record<string, string> = {
    al_dia:           'Al día',
    pendiente:        'Pendiente',
    parcial:          'Pago parcial',
    vencida:          'Vencida',
    sin_movimientos:  'Sin movimientos',
  };
  return labels[estado] ?? estado;
}

export function getEstadoCtaCteColor(
  estado: string,
): 'success' | 'warning' | 'error' | 'info' | 'default' {
  switch (estado) {
    case 'al_dia':          return 'success';
    case 'pendiente':       return 'warning';
    case 'parcial':         return 'info';
    case 'vencida':         return 'error';
    case 'sin_movimientos': return 'default';
    default:                return 'default';
  }
}

// ── Detalle (por cupón) ───────────────────────────────────────────────────────

export interface CuentaCorrienteDetalle {
  empresa_id: string;
  empresa_razon_social: string;
  empresa_cuit: string;
  periodo_id: string;
  periodo: string;
  periodo_descripcion: string | null;
  periodo_excepcional: boolean;
  cupon_id: string;
  cupon_codigo: string;
  cupon_estado: 'pendiente' | 'pagado' | 'vencido' | 'anulado';
  fecha_emision: string;
  fecha_vencimiento: string;
  monto_aporte: number;
  interes: number;
  /** monto_aporte + interes (columna generada en cupones) */
  monto_emitido: number;
  /** SUM(pagos acreditados) sobre este cupón */
  monto_pagado: number;
  /** monto_emitido - monto_pagado */
  saldo: number;
  esta_vencido: boolean;
  estado_cta_cte: EstadoCtaCteCupon;
}

// ── Resumen (por empresa) ─────────────────────────────────────────────────────

export interface CuentaCorrienteResumen {
  empresa_id: string;
  empresa_razon_social: string;
  empresa_cuit: string;
  empresa_estado: 'activa' | 'inactiva' | 'suspendida';
  cant_cupones: number;
  cant_pendientes: number;
  cant_vencidos: number;
  cant_pagados: number;
  monto_emitido_total: number;
  monto_pagado_total: number;
  /** Solo cuenta cupones pendientes/vencidos (no incluye pagados) */
  saldo_total: number;
  /** Fecha del cupón vencido más antiguo (NULL si no hay vencidos) */
  vencimiento_mas_antiguo: string | null;
  estado_general: EstadoCtaCteEmpresa;
}
